// Language Detection Service for Automatic Content Filtering
import { countryLanguageMapping } from './countryLanguageMapping';

// Types for language detection
export interface LanguagePreference {
  language_code: string;
  language_id: string;
}

export interface CountryLanguageInfo {
  country_code: string;
  languages: LanguagePreference[];
}

export interface UserLanguageContext {
  countryCode: string;
  browserLanguages: string[];
  selectedLanguage: LanguagePreference;
  fallbackToFirstLanguage: boolean;
}

export interface CloudflareTraceData {
  loc: string; // Country code
  ip: string;
  [key: string]: string;
}

class LanguageDetectionService {
  private static instance: LanguageDetectionService;
  private userLanguageContext: UserLanguageContext | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): LanguageDetectionService {
    if (!LanguageDetectionService.instance) {
      LanguageDetectionService.instance = new LanguageDetectionService();
    }
    return LanguageDetectionService.instance;
  }

  /**
   * Initialize language detection by calling Cloudflare trace API
   * and matching with browser preferences
   */
  public async initializeLanguageDetection(): Promise<UserLanguageContext> {
    try {
      if (this.userLanguageContext && this.isInitialized) {
        return this.userLanguageContext;
      }

      console.log('🌍 Starting language detection...');
      
      // Step 1: Get country from Cloudflare trace API
      const countryCode = await this.getCountryFromCloudflare();
      console.log(`🌍 User country detected: ${countryCode}`);

      // Step 2: Get browser language preferences
      const browserLanguages = this.getBrowserLanguagePreferences();
      console.log(`🌍 Browser languages: ${browserLanguages.join(', ')}`);

      // Step 3: Find matching language
      const selectedLanguage = this.findBestMatchingLanguage(countryCode, browserLanguages);
      console.log(`🌍 Selected language: ${selectedLanguage.language_code} (ID: ${selectedLanguage.language_id})`);

      // Step 4: Create user language context
      this.userLanguageContext = {
        countryCode,
        browserLanguages,
        selectedLanguage,
        fallbackToFirstLanguage: !browserLanguages.some(lang => 
          this.getCountryLanguages(countryCode).some(countryLang => 
            this.normalizeLanguageCode(lang) === countryLang.language_code
          )
        )
      };

      this.isInitialized = true;
      console.log('🌍 Language detection completed:', this.userLanguageContext);
      
      return this.userLanguageContext;
    } catch (error) {
      console.error('❌ Language detection failed:', error);
      
      // Fallback to English
      const fallbackLanguage: LanguagePreference = {
        language_code: 'en',
        language_id: '11703'
      };

      this.userLanguageContext = {
        countryCode: 'US',
        browserLanguages: ['en'],
        selectedLanguage: fallbackLanguage,
        fallbackToFirstLanguage: true
      };

      return this.userLanguageContext;
    }
  }

  /**
   * Get country code from Cloudflare trace API
   */
  private async getCountryFromCloudflare(): Promise<string> {
    try {
      const response = await fetch('https://www.infonium.org/cdn-cgi/trace', {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const traceText = await response.text();
      console.log('🌍 Cloudflare trace response:', traceText);

      // Parse the trace response
      const traceData: Record<string, string> = {};
      traceText.split('\n').forEach(line => {
        const [key, value] = line.split('=');
        if (key && value) {
          traceData[key.trim()] = value.trim();
        }
      });

      const countryCode = traceData.loc;
      if (!countryCode) {
        throw new Error('Country code not found in trace response');
      }

      return countryCode.toUpperCase();
    } catch (error) {
      console.error('❌ Failed to get country from Cloudflare:', error);
      // Fallback to US if detection fails
      return 'US';
    }
  }

  /**
   * Get browser language preferences from Accept-Language header simulation
   */
  private getBrowserLanguagePreferences(): string[] {
    try {
      const languages: string[] = [];
      
      // Get languages from navigator.languages (modern browsers)
      if (navigator.languages && navigator.languages.length > 0) {
        languages.push(...navigator.languages);
      } else if (navigator.language) {
        // Fallback to single language
        languages.push(navigator.language);
      }

      // Normalize language codes (remove country suffix and convert to lowercase)
      const normalizedLanguages = languages
        .map(lang => this.normalizeLanguageCode(lang))
        .filter((lang, index, arr) => arr.indexOf(lang) === index); // Remove duplicates

      console.log('🌍 Normalized browser languages:', normalizedLanguages);
      return normalizedLanguages;
    } catch (error) {
      console.error('❌ Failed to get browser languages:', error);
      return ['en']; // Fallback to English
    }
  }

  /**
   * Normalize language code (en-US -> en, pt-BR -> pt)
   */
  private normalizeLanguageCode(languageCode: string): string {
    return languageCode.split('-')[0].toLowerCase();
  }

  /**
   * Find the best matching language based on country and browser preferences
   */
  private findBestMatchingLanguage(
    countryCode: string, 
    browserLanguages: string[]
  ): LanguagePreference {
    try {
      const countryLanguages = this.getCountryLanguages(countryCode);
      
      if (countryLanguages.length === 0) {
        console.warn(`⚠️ No languages found for country: ${countryCode}, using English fallback`);
        return { language_code: 'en', language_id: '11703' };
      }

      // Try to find a language that matches browser preferences
      for (const browserLang of browserLanguages) {
        const normalizedBrowserLang = this.normalizeLanguageCode(browserLang);
        const matchingLanguage = countryLanguages.find(
          countryLang => countryLang.language_code === normalizedBrowserLang
        );
        
        if (matchingLanguage) {
          console.log(`✅ Found matching language: ${matchingLanguage.language_code}`);
          return matchingLanguage;
        }
      }

      // If no match found, use the first language for the country
      console.log(`📌 No browser language match found, using first country language: ${countryLanguages[0].language_code}`);
      return countryLanguages[0];
    } catch (error) {
      console.error('❌ Error finding matching language:', error);
      return { language_code: 'en', language_id: '11703' };
    }
  }

  /**
   * Get supported languages for a country
   */
  private getCountryLanguages(countryCode: string): LanguagePreference[] {
    const countryInfo = countryLanguageMapping[countryCode];
    return countryInfo?.languages || [];
  }

  /**
   * Get current user language context
   */
  public getCurrentLanguageContext(): UserLanguageContext | null {
    return this.userLanguageContext;
  }

  /**
   * Get current language category ID for API filtering
   */
  public getCurrentLanguageCategoryId(): string | null {
    return this.userLanguageContext?.selectedLanguage.language_id || null;
  }

  /**
   * Get current language code
   */
  public getCurrentLanguageCode(): string | null {
    return this.userLanguageContext?.selectedLanguage.language_code || null;
  }

  /**
   * Force refresh language detection
   */
  public async refreshLanguageDetection(): Promise<UserLanguageContext> {
    this.isInitialized = false;
    this.userLanguageContext = null;
    return this.initializeLanguageDetection();
  }

  /**
   * Manually set language (for testing or user override)
   */
  public setLanguage(languageCode: string, languageId: string): void {
    const selectedLanguage: LanguagePreference = {
      language_code: languageCode,
      language_id: languageId
    };

    this.userLanguageContext = {
      countryCode: this.userLanguageContext?.countryCode || 'US',
      browserLanguages: this.userLanguageContext?.browserLanguages || ['en'],
      selectedLanguage,
      fallbackToFirstLanguage: false
    };

    console.log('🌍 Language manually set:', this.userLanguageContext);
  }

  /**
   * Check if language detection is initialized
   */
  public isLanguageDetectionInitialized(): boolean {
    return this.isInitialized && this.userLanguageContext !== null;
  }
}

// Export singleton instance
export const languageDetectionService = LanguageDetectionService.getInstance(); 